import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check, ArrowLeft } from 'lucide-react';
import TemplateSelector from '../components/notifications/TemplateSelector';
import AudienceSelector from '../components/notifications/AudienceSelector';
import PreviewConfirm from '../components/notifications/PreviewConfirm';
import { WhatsAppBubble, useTemplatePreview } from '../components/notifications/WhatsAppPreview';
import { useApi } from '../utils/useApi';
import { useAuth } from '../contexts/AuthContext';

interface WhatsappTemplate {
  id: string;
  title: string;
  purpose?: string;
  language: string;
  templateName: string;
  content: string;
  parameterCount: number;
  sampleParams: Record<string, string>;
  isActive: boolean;
  createdAt: string;
}

interface WhatsappTemplateWithSchema extends WhatsappTemplate {
  parameterSchema?: Record<string, {
    source: 'member_data' | 'event_payload' | 'user_provided';
    key: string;
    description: string;
    placeholder?: string;
    required?: boolean;
  }>;
}

interface UserProvidedParam {
  key: string;
  label: string;
  placeholder: string;
  required: boolean;
}

const STEPS = [
  { id: 'template', name: 'Select Template' },
  { id: 'params', name: 'Fill Parameters' },
  { id: 'audience', name: 'Choose Audience' },
  { id: 'preview', name: 'Preview & Send' }
];

interface WhatsAppNotificationsPageProps {
  gymId: string;
  hideHeader?: boolean;
}

export default function WhatsAppNotificationsPage({ gymId: _gymId, hideHeader = false }: WhatsAppNotificationsPageProps) {
  const [currentStep, setCurrentStep] = useState<'template' | 'params' | 'audience' | 'preview'>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsappTemplateWithSchema | null>(null);
  const [userParams, setUserParams] = useState<Record<string, string>>({});
  const [selectedAudience, setSelectedAudience] = useState<any>(null);
  const [recipientCount, setRecipientCount] = useState<number>(0);
  const [excludedCount, setExcludedCount] = useState<number>(0);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [campaignLabel, setCampaignLabel] = useState<string>('');

  const { fetchWithAuth } = useApi();
  const { user } = useAuth();

  // Get current user's gym ID (tenant ID)
  const { data: userData } = useQuery({
    queryKey: ['user-context'],
    queryFn: async () => {
      return await fetchWithAuth('/api/auth/context');
    }
  });

  const tenantId = userData?.data?.accessibleGyms?.[0];

  // Live WhatsApp preview for the Fill Parameters step — re-renders (debounced)
  // as the admin types, with the real gym name substituted server-side.
  const paramsPreview = useTemplatePreview(selectedTemplate?.id, userParams, selectedTemplate?.content);

  const handleTemplateSelect = (template: WhatsappTemplate) => {
    setSelectedTemplate(template as WhatsappTemplateWithSchema);
    setUserParams({});
    setCampaignLabel(`Campaign - ${template.title}`);
    setCurrentStep('params');
  };

  const handleAudienceSelect = (audience: any) => {
    setSelectedAudience(audience);
  };

  const handleRecipientCount = (count: number, excluded: number) => {
    setRecipientCount(count);
    setExcludedCount(excluded);
  };

  const handleSend = async (audienceList: any[], scheduledAt?: Date | null, audienceModified?: boolean) => {
    if (!selectedTemplate || !tenantId || audienceList.length === 0) {
      toast.error('Please complete all steps before sending');
      return;
    }

    // If the admin removed people from the resolved recipient list on the
    // preview step, send to the exact remaining list instead of re-running the
    // filter (which would bring the removed members back).
    const effectiveAudience = audienceModified
      ? { type: 'users', numbers: audienceList.map(u => u.phoneNo) }
      : selectedAudience;

    setIsSending(true);
    try {
      // Create enhanced campaign
      const campaignResponse = await fetchWithAuth('/api/enhanced-campaigns', {
        method: 'POST',
        body: JSON.stringify({
          gymId: tenantId,
          templateId: selectedTemplate.id,
          name: campaignLabel,
          messageParams: userParams,
          targetGroup: effectiveAudience,
          scheduledAt: scheduledAt ? scheduledAt.toISOString() : null,
          createdBy: user?.id
        })
      });

      const campaign = campaignResponse.data;

      // Resolve audience and create messages
      const resolveResponse = await fetchWithAuth(`/api/enhanced-campaigns/${campaign.id}/resolve-audience`, {
        method: 'POST',
        body: JSON.stringify({
          audience: effectiveAudience,
          userParams: userParams
        })
      });

      const result = resolveResponse.data;

      // If scheduled, submit for approval and schedule
      if (scheduledAt) {
        await fetchWithAuth(`/api/enhanced-campaigns/${campaign.id}/submit`, {
          method: 'POST',
          body: JSON.stringify({
            submitted_by: user?.id
          })
        });

        await fetchWithAuth(`/api/enhanced-campaigns/${campaign.id}/approve`, {
          method: 'POST',
          body: JSON.stringify({
            approved_by: user?.id
          })
        });

        await fetchWithAuth(`/api/enhanced-campaigns/${campaign.id}/schedule`, {
          method: 'POST',
          body: JSON.stringify({
            scheduled_at: scheduledAt.toISOString(),
            scheduled_by: user?.id
          })
        });

        toast.success(`Campaign scheduled! ${result.createdCount} message${result.createdCount !== 1 ? 's' : ''} will be sent at ${scheduledAt.toLocaleString()}.`);
      } else {
        // Self-serve send: submit + auto-approve. Approving an unscheduled
        // campaign triggers the dispatcher immediately server-side.
        await fetchWithAuth(`/api/enhanced-campaigns/${campaign.id}/submit`, {
          method: 'POST',
          body: JSON.stringify({
            submitted_by: user?.id
          })
        });

        await fetchWithAuth(`/api/enhanced-campaigns/${campaign.id}/approve`, {
          method: 'POST',
          body: JSON.stringify({
            approved_by: user?.id
          })
        });

        toast.success(`Campaign created! ${result.createdCount} message${result.createdCount !== 1 ? 's' : ''} will be sent shortly.`);
      }

      // Reset form
      setSelectedTemplate(null);
      setUserParams({});
      setSelectedAudience(null);
      setRecipientCount(0);
      setExcludedCount(0);
      setCampaignLabel('');
      setCurrentStep('template');

    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create campaign');
    } finally {
      setIsSending(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 'params') {
      setCurrentStep('template');
    } else if (currentStep === 'audience') {
      setCurrentStep('params');
    } else if (currentStep === 'preview') {
      setCurrentStep('audience');
    }
  };

  const getStepStatus = (stepId: string) => {
    const stepIndex = STEPS.findIndex(s => s.id === stepId);
    const currentIndex = STEPS.findIndex(s => s.id === currentStep);
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  const getUserProvidedParams = (): UserProvidedParam[] => {
    if (!selectedTemplate?.parameterSchema) return [];

    const schemaEntries = Object.entries(selectedTemplate.parameterSchema);
    return schemaEntries
      .filter(([_, param]: [string, any]) => param.source === 'user_provided')
      .map(([key, param]: [string, any]) => ({
        key,
        label: param.description || key,
        placeholder: param.placeholder || `Enter ${param.description || key}`,
        required: param.required !== false
      }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WhatsApp Notifications</h1>
            <p className="text-gray-600">Manage your gym's communication</p>
          </div>
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
        </div>
      )}

      {/* Manual Campaign Flow */}
      <div className="space-y-4">
        {/* Step Progress — compact; completed steps are clickable to jump back */}
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-1">
          {STEPS.map((step, index) => {
            const status = getStepStatus(step.id);
            const stepIndex = STEPS.findIndex(s => s.id === currentStep);
            const isPast = index < stepIndex;

            return (
              <div key={step.id} className="flex items-center flex-shrink-0">
                <button
                  onClick={() => isPast && setCurrentStep(step.id as typeof currentStep)}
                  disabled={!isPast}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    status === 'current'
                      ? 'bg-blue-600 text-white'
                      : status === 'completed'
                        ? `bg-green-50 text-green-700 ${isPast ? 'hover:bg-green-100 cursor-pointer' : ''}`
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <span className={`flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${
                    status === 'current'
                      ? 'bg-white/25 text-white'
                      : status === 'completed'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-300 text-white'
                  }`}>
                    {status === 'completed' ? <Check className="h-2.5 w-2.5" /> : index + 1}
                  </span>
                  {step.name}
                </button>
                {index < STEPS.length - 1 && (
                  <div className={`w-4 sm:w-8 h-px mx-1 ${isPast ? 'bg-green-300' : 'bg-gray-200'}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          {currentStep === 'template' && (
            <TemplateSelector
              selectedTemplate={selectedTemplate}
              onTemplateSelect={handleTemplateSelect}
              gymId={tenantId}
            />
          )}

          {currentStep === 'params' && selectedTemplate && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Inputs */}
              <div className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedTemplate.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">Fill in your message below — the preview updates as you type.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Label</label>
                  <input
                    type="text"
                    value={campaignLabel}
                    onChange={(e) => setCampaignLabel(e.target.value)}
                    placeholder="Enter a label for this campaign"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Only visible to you, for tracking in campaign logs.</p>
                </div>

                {getUserProvidedParams().map((param) => (
                  <div key={param.key}>
                    <label className="block text-sm font-semibold text-gray-900 mb-1">
                      {param.label}
                      {param.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <textarea
                      value={userParams[param.key] || ''}
                      onChange={(e) => setUserParams(prev => ({ ...prev, [param.key]: e.target.value }))}
                      placeholder={param.placeholder}
                      rows={3}
                      className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                      required={param.required}
                      autoFocus
                    />
                  </div>
                ))}

                {getUserProvidedParams().length === 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 text-sm">
                      This template doesn't require any additional input. All data will be pulled from member profiles.
                    </p>
                  </div>
                )}
              </div>

              {/* Live preview */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
                <WhatsAppBubble
                  content={paramsPreview.previewContent}
                  isLoading={paramsPreview.isLoading}
                  className="max-w-sm lg:sticky lg:top-4"
                />
                <p className="text-xs text-gray-400 mt-2">This is how the message will appear in WhatsApp.</p>
              </div>
            </div>
          )}

          {currentStep === 'audience' && tenantId && (
            <AudienceSelector
              tenantId={tenantId}
              onAudienceSelect={handleAudienceSelect}
              onRecipientCount={handleRecipientCount}
            />
          )}
          {currentStep === 'audience' && !tenantId && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading audience options...</p>
            </div>
          )}

          {currentStep === 'preview' && (
            <PreviewConfirm
              template={selectedTemplate}
              audience={selectedAudience}
              tenantId={tenantId}
              recipientCount={recipientCount}
              excludedCount={excludedCount}
              userParams={userParams}
              onSend={handleSend}
              onBack={handleBack}
            />
          )}
        </div>

        {/* Navigation */}
        {currentStep !== 'template' && (
          <div className="flex justify-between">
            <button
              onClick={handleBack}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back
            </button>

            {currentStep === 'params' && (
              <button
                onClick={() => setCurrentStep('audience')}
                disabled={getUserProvidedParams().some(p => p.required && !userParams[p.key])}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Continue to Audience
              </button>
            )}

            {currentStep === 'audience' && recipientCount > 0 && (
              <button
                onClick={() => setCurrentStep('preview')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Continue to Preview
              </button>
            )}
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {isSending && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Creating Campaign</h3>
              <p className="text-gray-600">Please wait while we process your request...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
